import React, { FC, useMemo, useState } from 'react';
import {
  LeftOutlined,
  PlayCircleFilled,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileImageOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import { ChatMessage, parseFirestoreDate } from '../../types';
import {
  useGetChatMedia,
  ChatMediaItem,
  ChatFileItem,
  ChatLinkItem,
} from 'src/api/mutations/useGetChatMedia';
import { FullScreenImageModal } from 'src/components/Modals/FullScreenImageModal/FullScreenImageModal';
import { FullScreenVideoModal } from 'src/components/Modals/FullScreenVideoModal/FullScreenVideoModal';
import { ReactComponent as MediaFilesDownloadIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/media-links/media-links-files-download.svg';
import { ChatLinkCard } from '../ChatLinkCard/ChatLinkCard';
import { getInitialLinkMetadata } from '../../utils/linkMetadata';
import './MediaLinksFilesView.scss';

export interface MediaLinksFilesViewProps {
  chatId?: string;
  inquiryId?: string;
  messages?: ChatMessage[];
  onBack: () => void;
  initialTab?: 'media' | 'files' | 'links';
}

interface NormalizedMediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
  fileName?: string;
  date: Date;
}

interface NormalizedFileItem {
  id: string;
  url: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sizeLabel?: string;
  date: Date;
}

interface NormalizedLinkItem {
  id: string;
  url: string;
  domain: string;
  title?: string;
  description?: string;
  contextText?: string;
  date: Date;
}

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

interface FileTypeMeta {
  icon: React.ReactNode;
  bg: string;
  color: string;
  label: string;
}

const getFileMeta = (mimeType?: string, fileName?: string): FileTypeMeta => {
  const ext = fileName ? fileName.split('.').pop()?.toLowerCase() : '';
  const mime = mimeType?.toLowerCase() || '';

  if (ext === 'pdf' || mime === 'application/pdf') {
    return {
      icon: <FilePdfOutlined style={{ color: '#ef4444' }} />,
      bg: 'rgba(239, 68, 68, 0.15)',
      color: '#ef4444',
      label: 'PDF',
    };
  }
  if (
    ['doc', 'docx'].includes(ext || '') ||
    mime.includes('word') ||
    mime.includes('officedocument.wordprocessingml')
  ) {
    return {
      icon: <FileWordOutlined style={{ color: '#3b82f6' }} />,
      bg: 'rgba(59, 130, 246, 0.15)',
      color: '#3b82f6',
      label: 'Word',
    };
  }
  if (
    ['xls', 'xlsx', 'csv'].includes(ext || '') ||
    mime.includes('excel') ||
    mime.includes('spreadsheetml') ||
    mime === 'text/csv'
  ) {
    return {
      icon: <FileExcelOutlined style={{ color: '#10b981' }} />,
      bg: 'rgba(16, 185, 129, 0.15)',
      color: '#10b981',
      label: ext === 'csv' ? 'CSV' : 'Excel',
    };
  }
  if (
    ['ppt', 'pptx'].includes(ext || '') ||
    mime.includes('presentation') ||
    mime.includes('powerpoint')
  ) {
    return {
      icon: <FilePptOutlined style={{ color: '#f97316' }} />,
      bg: 'rgba(249, 115, 22, 0.15)',
      color: '#f97316',
      label: 'PPT',
    };
  }
  if (
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '') ||
    mime.includes('zip') ||
    mime.includes('compressed')
  ) {
    return {
      icon: <FileZipOutlined style={{ color: '#a855f7' }} />,
      bg: 'rgba(168, 85, 247, 0.15)',
      color: '#a855f7',
      label: 'Archive',
    };
  }
  if (
    ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext || '') ||
    mime.startsWith('image/')
  ) {
    return {
      icon: <FileImageOutlined style={{ color: '#06b6d4' }} />,
      bg: 'rgba(6, 182, 212, 0.15)',
      color: '#06b6d4',
      label: ext ? ext.toUpperCase() : 'Image',
    };
  }
  return {
    icon: <FileTextOutlined style={{ color: '#60a5fa' }} />,
    bg: 'rgba(96, 165, 250, 0.15)',
    color: '#60a5fa',
    label: ext ? ext.toUpperCase() : 'Document',
  };
};

export const MediaLinksFilesView: FC<MediaLinksFilesViewProps> = ({
  chatId,
  inquiryId,
  messages = [],
  onBack,
  initialTab = 'media',
}) => {
  const [activeTab, setActiveTab] = useState<'media' | 'files' | 'links'>(initialTab);

  // Preview modals
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  // Fetch all chat media without pagination from Cloud Function backend
  const { data: serverMediaData, isLoading: isServerLoading } = useGetChatMedia(
    chatId || '',
    inquiryId,
    Boolean(chatId),
  );

  // Normalize and merge media items (server + realtime messages)
  const mediaItems = useMemo(() => {
    const map = new Map<string, NormalizedMediaItem>();

    // 1. Seed from backend API
    if (serverMediaData?.media) {
      serverMediaData.media.forEach((item: ChatMediaItem) => {
        map.set(item.id, {
          id: item.id,
          url: item.url,
          type: item.type,
          fileName: item.file_name,
          date: new Date(item.date),
        });
      });
    }

    // 2. Overlay from realtime messages
    messages.forEach((msg) => {
      if (msg.attachments && msg.attachments.length > 0) {
        msg.attachments.forEach((att, idx) => {
          if (att.kind === 'media' || att.type === 'image' || att.type === 'video') {
            const id = `${msg.id}_${idx}`;
            map.set(id, {
              id,
              url: att.url,
              type: att.type === 'video' ? 'video' : 'image',
              fileName: att.file_name,
              date: parseFirestoreDate(msg.send_time),
            });
          }
        });
      } else if (msg.message_type === 'image' || msg.message_type === 'video') {
        if (msg.media_url) {
          map.set(msg.id, {
            id: msg.id,
            url: msg.media_url,
            type: msg.message_type as 'image' | 'video',
            fileName: msg.file_name,
            date: parseFirestoreDate(msg.send_time),
          });
        }
      }
    });

    const list = Array.from(map.values());
    return list.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [serverMediaData, messages]);

  // Normalize and merge file items (server + realtime messages)
  const fileItems = useMemo(() => {
    const map = new Map<string, NormalizedFileItem>();

    // 1. Seed from backend API
    if (serverMediaData?.files) {
      serverMediaData.files.forEach((item: ChatFileItem) => {
        map.set(item.id, {
          id: item.id,
          url: item.url,
          fileName: item.file_name || 'Unknown File',
          mimeType: item.mime_type || '',
          sizeBytes: item.size_bytes || 0,
          sizeLabel: item.size_label,
          date: new Date(item.date),
        });
      });
    }

    // 2. Overlay from realtime messages
    messages.forEach((msg) => {
      if (msg.attachments && msg.attachments.length > 0) {
        msg.attachments.forEach((att, idx) => {
          if (att.kind === 'file') {
            const id = `${msg.id}_${idx}`;
            map.set(id, {
              id,
              url: att.url,
              fileName: att.file_name || 'Unknown File',
              mimeType: att.mime_type || '',
              sizeBytes: att.size_bytes || 0,
              sizeLabel: att.size_label,
              date: parseFirestoreDate(msg.send_time),
            });
          }
        });
      } else if (msg.message_type === 'pdf') {
        const legacyUrl =
          (msg as { file_url?: string; media_url?: string }).file_url || msg.media_url;
        if (legacyUrl) {
          map.set(msg.id, {
            id: msg.id,
            url: legacyUrl,
            fileName: msg.file_name || 'Document.pdf',
            mimeType: 'application/pdf',
            sizeBytes: typeof msg.file_size === 'number' ? msg.file_size : 0,
            date: parseFirestoreDate(msg.send_time),
          });
        }
      }
    });

    const list = Array.from(map.values());
    return list.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [serverMediaData, messages]);

  // Normalize and merge link items (server + realtime messages)
  const linkItems = useMemo(() => {
    const map = new Map<string, NormalizedLinkItem>();
    const URL_REGEX = /https?:\/\/[^\s]+[^\s.,;:!?'")]/g;

    // 1. Seed from backend API
    if (serverMediaData?.links) {
      serverMediaData.links.forEach((item: ChatLinkItem) => {
        const initMeta = getInitialLinkMetadata(item.url);
        map.set(item.id, {
          id: item.id,
          url: item.url,
          domain: item.domain || initMeta.domain,
          title: item.title || initMeta.title,
          description: item.description || initMeta.description,
          date: new Date(item.date),
        });
      });
    }

    // 2. Overlay from realtime messages
    messages.forEach((msg) => {
      const bodyText = msg.body || msg.inquiryDetail || msg.inquirySubject || msg.inquiry_text;
      if (bodyText && bodyText.trim().length > 0) {
        const matches = bodyText.match(URL_REGEX);
        if (matches) {
          const seenUrls = new Set<string>();

          matches.forEach((url) => {
            const normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url;
            if (!seenUrls.has(normalizedUrl)) {
              seenUrls.add(normalizedUrl);
              const preview = msg.link_preview;
              const initMeta = getInitialLinkMetadata(url, bodyText);
              const id = `${msg.id}_${normalizedUrl}`;
              map.set(id, {
                id,
                url,
                domain: preview?.domain || initMeta.domain,
                title: preview?.title || initMeta.title,
                description: preview?.description || initMeta.description,
                contextText: bodyText,
                date: parseFirestoreDate(msg.send_time),
              });
            }
          });
        }
      }
    });

    const list = Array.from(map.values());
    return list.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [serverMediaData, messages]);

  // Group by Date for Media
  const groupedMedia = useMemo(() => {
    const groups: { label: string; items: NormalizedMediaItem[] }[] = [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    mediaItems.forEach((item) => {
      const itemDate = new Date(item.date.getFullYear(), item.date.getMonth(), item.date.getDate());
      let label = '';
      if (itemDate.getTime() === today.getTime()) {
        label = 'Today';
      } else if (itemDate.getTime() === yesterday.getTime()) {
        label = 'Yesterday';
      } else {
        label = item.date.toLocaleDateString('en-GB', { month: 'short' });
      }

      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.label === label) {
        lastGroup.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
    });

    return groups;
  }, [mediaItems]);

  // Group by Date for Files
  const groupedFiles = useMemo(() => {
    const groups: { label: string; items: NormalizedFileItem[] }[] = [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    fileItems.forEach((item) => {
      const itemDate = new Date(item.date.getFullYear(), item.date.getMonth(), item.date.getDate());
      let label = '';
      if (itemDate.getTime() === today.getTime()) {
        label = 'Today';
      } else if (itemDate.getTime() === yesterday.getTime()) {
        label = 'Yesterday';
      } else {
        label = item.date.toLocaleDateString('en-GB', { month: 'short' });
      }

      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.label === label) {
        lastGroup.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
    });

    return groups;
  }, [fileItems]);

  // Group by Date for Links
  const groupedLinks = useMemo(() => {
    const groups: { label: string; items: NormalizedLinkItem[] }[] = [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    linkItems.forEach((item) => {
      const itemDate = new Date(item.date.getFullYear(), item.date.getMonth(), item.date.getDate());
      let label = '';
      if (itemDate.getTime() === today.getTime()) {
        label = 'Today';
      } else if (itemDate.getTime() === yesterday.getTime()) {
        label = 'Yesterday';
      } else {
        label = item.date.toLocaleDateString('en-GB', { month: 'short' });
      }

      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.label === label) {
        lastGroup.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
    });

    return groups;
  }, [linkItems]);

  const openPreview = (item: NormalizedMediaItem) => {
    if (item.type === 'video') {
      setPreviewVideoUrl(item.url);
      return;
    }
    setPreviewUrl(item.url);
    setPreviewOpen(true);
  };

  const downloadFile = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <div className="media-links-files-view">
      <div className="mlf-header">
        <button className="back-nav" onClick={onBack} aria-label="Back">
          <LeftOutlined />
        </button>
        <h2>Media, links & files</h2>
      </div>

      <div className="mlf-segmented-control">
        <button
          className={activeTab === 'media' ? 'active' : ''}
          onClick={() => setActiveTab('media')}
        >
          Media
        </button>
        <button
          className={activeTab === 'files' ? 'active' : ''}
          onClick={() => setActiveTab('files')}
        >
          Files
        </button>
        <button
          className={activeTab === 'links' ? 'active' : ''}
          onClick={() => setActiveTab('links')}
        >
          Links
        </button>
      </div>

      <div className="mlf-content">
        {isServerLoading && mediaItems.length === 0 && fileItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: '#F59E0B' }} spin />} />
          </div>
        ) : (
          <>
            {activeTab === 'media' && (
              <div className="media-tab">
                {groupedMedia.length === 0 ? (
                  <div className="empty-state">No media in this conversation</div>
                ) : (
                  groupedMedia.map((group) => (
                    <div key={group.label} className="media-group">
                      <div className="group-label">{group.label}</div>
                      <div className="media-grid">
                        {group.items.map((item) => (
                          <div
                            key={item.id}
                            className={`media-item ${item.type}`}
                            onClick={() => openPreview(item)}
                          >
                            {item.type === 'video' ? (
                              <div className="video-thumbnail">
                                <video src={item.url} preload="metadata" />
                                <div className="play-icon-overlay">
                                  <PlayCircleFilled />
                                </div>
                              </div>
                            ) : (
                              <img src={item.url} alt="Media attachment" loading="lazy" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'files' && (
              <div className="files-tab">
                {groupedFiles.length === 0 ? (
                  <div className="empty-state">No files in this conversation</div>
                ) : (
                  groupedFiles.map((group) => (
                    <div key={group.label} className="file-group">
                      <div className="group-label">{group.label}</div>
                      <div className="file-list">
                        {group.items.map((item) => {
                          const meta = getFileMeta(item.mimeType, item.fileName);
                          return (
                            <div key={item.id} className="file-item">
                              <div className="file-icon-container" style={{ background: meta.bg }}>
                                {meta.icon}
                              </div>
                              <div className="file-details">
                                <span className="file-name">{item.fileName}</span>
                                <span className="file-meta">
                                  {meta.label} • {item.sizeLabel || formatBytes(item.sizeBytes)}
                                </span>
                              </div>
                              <button
                                className="download-btn"
                                onClick={() => downloadFile(item.url)}
                                title={`Download ${item.fileName}`}
                                aria-label={`Download ${item.fileName}`}
                              >
                                <MediaFilesDownloadIcon />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'links' && (
              <div className="links-tab">
                {groupedLinks.length === 0 ? (
                  <div className="empty-state">No links in this conversation</div>
                ) : (
                  groupedLinks.map((group) => (
                    <div key={group.label} className="link-group">
                      <div className="group-label">{group.label}</div>
                      <div className="link-list">
                        {group.items.map((item) => (
                          <ChatLinkCard
                            key={item.id}
                            url={item.url}
                            contextText={item.contextText}
                            initialTitle={item.title}
                            initialDescription={item.description}
                            variant="list"
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Preview Modal */}
      <FullScreenImageModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={previewUrl}
      />

      {/* Video Preview Modal */}
      <FullScreenVideoModal
        isOpen={Boolean(previewVideoUrl)}
        onClose={() => setPreviewVideoUrl(null)}
        videoUrl={previewVideoUrl || ''}
      />
    </div>
  );
};
